/**
 * WalletSelector Component
 *
 * Allows users to select recipients from a table of Hedera wallets
 * or via bulk paste functionality.
 *
 * @component
 */

import React, { useState, useMemo } from "react";
import { WALLETS_PER_PAGE, MAX_RECIPIENTS, SELECTION_MODE } from "../constants";
import { colors, gradients, commonStyles } from "../styles";
import { PrimaryButton, DangerButton } from "./Button";
import BulkPasteSelector from "./BulkPasteSelector";

const WalletSelector = ({
  wallets,
  selectedWallets,
  onToggleWallet,
  onSelectAll,
  onDeselectAll,
  onBulkAdd,
  loading,
  searchQuery,
  onSearchChange,
  bulkInputText,
  setBulkInputText,
  bulkParseResult,
  setBulkParseResult,
  selectionSource,
  setSelectionSource,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectionMode, setSelectionMode] = useState(SELECTION_MODE.TABLE);

  // Handle mode change with confirmation if selections exist from different source
  const handleModeChange = (newMode) => {
    const hasSelections = selectedWallets.size > 0;
    const switchingToTable = newMode === SELECTION_MODE.TABLE;
    const switchingToPaste = newMode === SELECTION_MODE.PASTE;

    if (hasSelections) {
      if (switchingToTable && selectionSource === "paste") {
        const proceed = confirm(
          `You have ${selectedWallets.size} recipient(s) selected from the Paste List.\n\n` +
            `Switching to Table selection will clear your current selection.\n\n` +
            `Do you want to continue?`,
        );
        if (!proceed) return;
        onDeselectAll();
        setBulkInputText("");
        setBulkParseResult(null);
        setSelectionSource(null);
      } else if (switchingToPaste && selectionSource === "table") {
        const proceed = confirm(
          `You have ${selectedWallets.size} recipient(s) selected from the Table.\n\n` +
            `Switching to Paste List will clear your current selection.\n\n` +
            `Do you want to continue?`,
        );
        if (!proceed) return;
        onDeselectAll();
        setSelectionSource(null);
      }
    }

    setSelectionMode(newMode);
  };

  const filteredWallets = useMemo(() => {
    if (!searchQuery) return wallets;
    const query = searchQuery.toLowerCase();
    return wallets.filter((w) => w.email?.toLowerCase().includes(query));
  }, [wallets, searchQuery]);

  // Pagination logic
  const totalPages = Math.ceil(filteredWallets.length / WALLETS_PER_PAGE);
  const paginatedWallets = useMemo(() => {
    const startIndex = (currentPage - 1) * WALLETS_PER_PAGE;
    return filteredWallets.slice(startIndex, startIndex + WALLETS_PER_PAGE);
  }, [filteredWallets, currentPage]);

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const allSelected =
    filteredWallets.length > 0 &&
    filteredWallets.every((w) => selectedWallets.has(w.address));
  const pageAllSelected =
    paginatedWallets.length > 0 &&
    paginatedWallets.every((w) => selectedWallets.has(w.address));

  return (
    <div
      className="wallet-selector panel"
      style={{
        ...commonStyles.panel,
        marginBottom: "20px",
      }}
    >
      {/* Header */}
      <h3
        style={{
          ...commonStyles.panelHeader,
          display: "flex",
          alignItems: "center",
        }}
      >
        Select Recipients from Hedera Wallets
        <span
          style={{
            marginLeft: "12px",
            fontSize: "13px",
            fontWeight: 400,
            color: "rgba(255,255,255,0.8)",
            background: "rgba(255,255,255,0.15)",
            padding: "4px 10px",
            borderRadius: "12px",
          }}
        >
          Total: {wallets.length} wallets
        </span>
      </h3>

      {/* Selection Mode Tabs */}
      <div
        style={{
          display: "flex",
          background: colors.bgMedium,
          padding: 0,
          borderBottom: `2px solid ${colors.accent}`,
        }}
      >
        <button
          type="button"
          onClick={() => handleModeChange(SELECTION_MODE.TABLE)}
          style={{
            padding: "14px 28px",
            background:
              selectionMode === SELECTION_MODE.TABLE
                ? colors.primary
                : "transparent",
            color:
              selectionMode === SELECTION_MODE.TABLE
                ? colors.white
                : colors.primary,
            border: "none",
            borderRight: `1px solid ${colors.borderLight}`,
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          Select from Table
        </button>
        <button
          type="button"
          onClick={() => handleModeChange(SELECTION_MODE.PASTE)}
          style={{
            padding: "14px 28px",
            background:
              selectionMode === SELECTION_MODE.PASTE
                ? colors.primary
                : "transparent",
            color:
              selectionMode === SELECTION_MODE.PASTE
                ? colors.white
                : colors.primary,
            border: "none",
            fontSize: "14px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          Paste List
        </button>
      </div>

      {/* Selection Summary Bar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 20px",
          background: colors.white,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <span style={{ fontSize: "13px", color: colors.gray600 }}>
          Recipients selected for airdrop
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span
            style={{
              padding: "6px 14px",
              background: selectedWallets.size > 0 ? colors.accent : "#6c757d",
              color: colors.white,
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            {selectedWallets.size} selected
          </span>
          {selectedWallets.size > 0 && (
            <DangerButton
              onClick={onDeselectAll}
              style={{ padding: "6px 14px", fontSize: "13px" }}
            >
              Clear All
            </DangerButton>
          )}
        </div>
      </div>

      {/* Paste Mode */}
      {selectionMode === SELECTION_MODE.PASTE && (
        <BulkPasteSelector
          wallets={wallets}
          onAddWallets={onBulkAdd}
          selectedCount={selectedWallets.size}
          maxRecipients={MAX_RECIPIENTS}
          inputText={bulkInputText}
          setInputText={setBulkInputText}
          parseResult={bulkParseResult}
          setParseResult={setBulkParseResult}
        />
      )}

      {/* Table Mode */}
      {selectionMode === SELECTION_MODE.TABLE && (
        <>
          {/* Search and Actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "14px 20px",
              background: colors.gray100,
              borderBottom: `1px solid ${colors.border}`,
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <div style={{ flex: 1, minWidth: "200px", maxWidth: "350px" }}>
              <input
                type="email"
                placeholder="Search by email, name, or username..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                style={commonStyles.input}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <PrimaryButton
                onClick={() => onSelectAll(paginatedWallets)}
                disabled={
                  loading || paginatedWallets.length === 0 || pageAllSelected
                }
                style={{ padding: "10px 18px", fontSize: "13px" }}
              >
                Select Page
              </PrimaryButton>
              <PrimaryButton
                onClick={
                  allSelected
                    ? onDeselectAll
                    : () => onSelectAll(filteredWallets)
                }
                disabled={loading || filteredWallets.length === 0}
                style={{ padding: "10px 18px", fontSize: "13px" }}
              >
                {allSelected ? "Deselect All" : "Select All"}
              </PrimaryButton>
            </div>
          </div>

          {/* Table Content */}
          {loading ? (
            <div className="wallet-table-loading">
              <div className="spinner"></div>
              <span>Loading wallets...</span>
            </div>
          ) : filteredWallets.length === 0 ? (
            <div className="wallet-table-empty">
              <p>No wallets found matching your search</p>
            </div>
          ) : (
            <>
              <div className="index_table_container">
                <table className="index_table wallet-table">
                  <thead>
                    <tr>
                      <th className="col col-selectable">
                        <input
                          type="checkbox"
                          checked={pageAllSelected}
                          onChange={() =>
                            pageAllSelected
                              ? paginatedWallets.forEach(
                                  (w) =>
                                    selectedWallets.has(w.address) &&
                                    onToggleWallet(w),
                                )
                              : onSelectAll(paginatedWallets)
                          }
                          title="Select all on this page"
                        />
                      </th>
                      <th className="col col-id sortable">ID</th>
                      <th className="col col-address sortable">
                        Wallet Address
                      </th>
                      <th className="col col-email sortable">User Email</th>
                      <th className="col col-name sortable">User Name</th>
                      <th className="col col-status sortable">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedWallets.map((wallet, index) => (
                      <tr
                        key={wallet.address}
                        className={`${
                          selectedWallets.has(wallet.address) ? "selected" : ""
                        } ${index % 2 === 0 ? "even" : "odd"}`}
                        onClick={() => onToggleWallet(wallet)}
                      >
                        <td className="col col-selectable">
                          <input
                            type="checkbox"
                            checked={selectedWallets.has(wallet.address)}
                            onChange={() => onToggleWallet(wallet)}
                            disabled={
                              !selectedWallets.has(wallet.address) &&
                              selectedWallets.size >= MAX_RECIPIENTS
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="col col-id">{wallet.id || "-"}</td>
                        <td className="col col-address">
                          <code>{wallet.address}</code>
                        </td>
                        <td className="col col-email">{wallet.email || "-"}</td>
                        <td className="col col-name">{wallet.name || "-"}</td>
                        <td className="col col-status">
                          <span
                            className={`status_tag ${
                              wallet.status || "active"
                            }`}
                          >
                            {wallet.status || "active"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div
                className="pagination_container"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "14px 16px",
                  background: colors.gray200,
                  borderTop: `1px solid ${colors.border}`,
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div style={{ fontSize: "13px", color: colors.gray600 }}>
                  Displaying wallets{" "}
                  <b style={{ color: colors.primary }}>
                    {(currentPage - 1) * WALLETS_PER_PAGE + 1}
                  </b>{" "}
                  -{" "}
                  <b style={{ color: colors.primary }}>
                    {Math.min(
                      currentPage * WALLETS_PER_PAGE,
                      filteredWallets.length,
                    )}
                  </b>{" "}
                  of{" "}
                  <b style={{ color: colors.primary }}>
                    {filteredWallets.length}
                  </b>{" "}
                  in total
                </div>
                {totalPages > 1 && (
                  <nav
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span
                      onClick={() =>
                        currentPage > 1 && setCurrentPage(currentPage - 1)
                      }
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 14px",
                        height: "36px",
                        fontSize: "14px",
                        fontWeight: 600,
                        color:
                          currentPage === 1 ? colors.gray500 : colors.white,
                        background:
                          currentPage === 1 ? colors.gray300 : colors.primary,
                        border: "none",
                        borderRadius: "4px",
                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        userSelect: "none",
                      }}
                    >
                      ‹ Prev
                    </span>
                    {[...Array(Math.min(totalPages, 10))].map((_, i) => {
                      let pageNum;
                      if (totalPages <= 10) {
                        pageNum = i + 1;
                      } else if (currentPage <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 4) {
                        pageNum = totalPages - 9 + i;
                      } else {
                        pageNum = currentPage - 4 + i;
                      }
                      return (
                        <span
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "36px",
                            height: "36px",
                            padding: "0 8px",
                            fontSize: "14px",
                            fontWeight: pageNum === currentPage ? 700 : 600,
                            color:
                              pageNum === currentPage
                                ? colors.white
                                : colors.primary,
                            background:
                              pageNum === currentPage
                                ? colors.primary
                                : colors.white,
                            border: `2px solid ${
                              pageNum === currentPage
                                ? colors.primary
                                : colors.borderLight
                            }`,
                            borderRadius: "4px",
                            cursor:
                              pageNum === currentPage ? "default" : "pointer",
                            userSelect: "none",
                          }}
                        >
                          {pageNum}
                        </span>
                      );
                    })}
                    <span
                      onClick={() =>
                        currentPage < totalPages &&
                        setCurrentPage(currentPage + 1)
                      }
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 14px",
                        height: "36px",
                        fontSize: "14px",
                        fontWeight: 600,
                        color:
                          currentPage === totalPages
                            ? colors.gray500
                            : colors.white,
                        background:
                          currentPage === totalPages
                            ? colors.gray300
                            : colors.primary,
                        border: "none",
                        borderRadius: "4px",
                        cursor:
                          currentPage === totalPages
                            ? "not-allowed"
                            : "pointer",
                        userSelect: "none",
                      }}
                    >
                      Next ›
                    </span>
                  </nav>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default WalletSelector;
